/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Tabs, Tab, Box, Typography, styled, Grid, Stack } from '@mui/material';
import { IoIosClose } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import EmbedLink from './Components/embedLink';
import SystemUpload from './Components/systemUpload';
import MediaDrive from './Components/mediaDrive';
import ResourceContent from './Components/ResourceContent';
import CustomSearch from '../../../../../../../../UiComponents/Search/CustomSearch';
import { useSelector } from 'react-redux';
import { Storage } from 'aws-amplify';
import { newNewsFeedUploadFiles } from '../../../../../../../../libs/HelperContent';
import useContentPermissions from '../ContentCards/useContentPermissions';
import NoData from '../../../../../../../../components/Common/NoData';
import { toast } from 'react-toastify';
import useUpdateStorage from '../../../../../../../LicenseSubscription/customHooks/useGetStorage';

const TabPanel = ({ children, value, index }) =>
{
	return (
		<div
			role='tabpanel'
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			style={{ width: '100%', flexGrow: 1 }}
		>
			{value === index && (
				<Box sx={{ pt: 3, width: '100%', display: 'flex', justifyContent: 'center', height: '100%' }}>{children}</Box>
			)}
		</div>
	);
};

const CustomTabs = styled(Tabs)({
	'.MuiTabs-indicator' : {
		backgroundColor : '#2196f3',
		height          : '0'
	}
});

const CustomTab = styled(Tab)({
	textTransform    : 'none',
	minWidth         : 120,
	color            : '#000',
	'&.Mui-selected' : {
		color           : '#000',
		border          : '1px solid none',
		backgroundColor : '#fff',
		margin          : 10,
		boxShadow       : '0px 4px 10px rgba(0, 0, 0, 0.1)',
		borderRadius    : 13
	},
	'&:hover' : {
		color   : '#000',
		opacity : 1
	}
});

interface UploadDialogProps {
open: boolean;
onClose: () => void;
fileType: string;
note: string;
Embednote: string;
showResourcesTab?: boolean;
// eslint-disable-next-line no-unused-vars
onFileSelect?: ((file, source: string, key?: string) => void) | undefined;
fromCreation?:string;
}

const UploadDialog: React.FC<UploadDialogProps> = (props) =>
{
	const { open, onClose, fileType, note, onFileSelect, Embednote, showResourcesTab = true, fromCreation } = props;
	const userData = useSelector((state:any) => state?.mysession?.session);
	const [ tabValue, setTabValue ] = useState(0);
	const [ selectedItems, setSelectedItems ] = useState<any[]>([]);
	const [ isUploading, setIsUploading ] = useState(false);

	// Get upload permissions
	const {
		isLocalUploadEnabled,
		isEmbedUploadEnabled,
		isDriveUploadEnabled,
		uploadSizeMBLimit
	} = useContentPermissions();

	// Get storage update function
	const setStorageData = useUpdateStorage();

	// Check if all upload options are disabled
	const allUploadOptionsDisabled = !isLocalUploadEnabled && !isEmbedUploadEnabled && !isDriveUploadEnabled;

	const handleChange = (event, newValue) =>
	{
		// Block tab change when uploading
		if (isUploading) return;

		setTabValue(newValue);
		setSelectedItems([]);
	};

	// Create array of enabled tabs in order
	const enabledTabs: any[] = [];
	const tabIndexMap: any = {};

	if (isLocalUploadEnabled)
	{
		tabIndexMap.system = enabledTabs.length;
		enabledTabs.push({ key: 'system', label: 'system', component: 'SystemUpload' });
	}
	if (isDriveUploadEnabled)
	{
		tabIndexMap.mediaDrive = enabledTabs.length;
		enabledTabs.push({ key: 'mediaDrive', label: 'mediaDrive', component: 'MediaDrive' });
	}
	if (isEmbedUploadEnabled)
	{
		tabIndexMap.url = enabledTabs.length;
		enabledTabs.push({ key: 'url', label: 'URL', component: 'EmbedLink' });
	}

	// Get tab indices
	const systemTabIndex = tabIndexMap.system ?? -1;
	const mediaDriveTabIndex = tabIndexMap.mediaDrive ?? -1;
	const urlTabIndex = tabIndexMap.url ?? -1;

	const handleClose = () =>
	{
		// Block close action when uploading
		if (isUploading)
		{
			return;
		}

		setTabValue(0);
		setSelectedItems([]);
		onClose?.();
	};

	const handleUpload = async () =>
	{
		setIsUploading(true);
		try
		{
			await Storage.configure({
				bucket       : process.env.REACT_APP_S3_BUCKET,
				region       : process.env.REACT_APP_S3_BUCKET_REGION,
				customPrefix : { public: '' }
			});

			const totalSize = selectedItems?.reduce((acc, item) => acc + item?.size, 0);

			if (totalSize > uploadSizeMBLimit * 1024 * 1024)
			{
				return toast.error(`File size exceeds the limit ${uploadSizeMBLimit}MB`);
			}

			// Call storage API to add space before upload
			try
			{
				const storageAddResult = await setStorageData({
					action         : 'add',
					size           : totalSize,
					ownerCompanyId : userData?.companyId
				});

				// Check if storage allocation failed
				if (storageAddResult?.statusCode === 507)
				{
					toast.error('Storage limit exceeded. Please contact your admin.', {
						position  : toast.POSITION.TOP_RIGHT,
						className : 'toast-message'
					});
					setIsUploading(false);

					return; // Storage limit exceeded, don't proceed with upload
				}

				// Check for any other storage API errors
				if (storageAddResult?.statusCode && storageAddResult.statusCode >= 400)
				{
					toast.error('Storage allocation failed. Please try again.', {
						position  : toast.POSITION.TOP_RIGHT,
						className : 'toast-message'
					});
					setIsUploading(false);

					return; // Storage allocation failed, don't proceed with upload
				}
			}
			catch (storageError)
			{
				console.error('Storage API error:', storageError);
				toast.error('Storage allocation failed. Please try again.', {
					position  : toast.POSITION.TOP_RIGHT,
					className : 'toast-message'
				});
				setIsUploading(false);

				return; // Storage API failed, don't proceed with upload
			}

			let uploadSuccess = true;
			const uploadedFiles: any[] = [];

			for (const item of selectedItems)
			{
				if (item?.key)
				{
					try
					{
						const response = (await Storage.get(item.key, {
							download : true
						}) as unknown) as { Body: Blob };

						const file = new File([ response.Body ], item.name, { type: item.type });

						if (fromCreation && [ 'image', 'video', 'audio', 'document' ]?.includes(fromCreation))
						{
							await onFileSelect?.(file, 'media_drive', item.key);
							uploadedFiles.push(item);
						}
						else
						{
							const path = `${userData?.companyId}/resource`;

							const uploadResults = await newNewsFeedUploadFiles([ file ], path);

							if (uploadResults && uploadResults.length > 0)
							{
								const uploadedFile = uploadResults[0];

								await onFileSelect?.(uploadedFile.url, 'media_drive', uploadedFile.key);
								uploadedFiles.push(item);
							}
							else
							{
								uploadSuccess = false;
								break;
							}
						}
					}
					catch (error)
					{
						console.error('Error uploading file:', error);
						uploadSuccess = false;
						break;
					}
				}
			}

			// If upload failed, remove the allocated storage
			if (!uploadSuccess)
			{
				await setStorageData({
					action         : 'remove',
					size           : totalSize,
					ownerCompanyId : userData?.companyId
				});
				toast.error('Upload failed. Storage allocation has been reverted.');
			}

			handleClose();
			setIsUploading(false);
		}
		catch (error)
		{
			console.error('Error processing files====123====', error);

			// If there was an error, try to remove the allocated storage
			const totalSize = selectedItems?.reduce((acc, item) => acc + item?.size, 0);

			try
			{
				await setStorageData({
					action         : 'remove',
					size           : totalSize,
					ownerCompanyId : userData?.companyId
				});
			}
			catch (storageError)
			{
				console.error('Error reverting storage allocation:', storageError);
			}

			setIsUploading(false);
			throw error;
		}
	};

	return (
		<Dialog
			open={open}
			onClose={isUploading ? undefined : handleClose}
			fullWidth
			maxWidth='md'
			sx={{ padding: '24px', zIndex: 1025 }}
		>
			<DialogTitle display={'flex'} justifyContent={'flex-end'} flexWrap={'wrap'}>
				<Grid container width={'100%'} direction={'row'}>
					<Grid item xs minWidth={0}>
						<Typography color={'#000'} width={'100%'} fontWeight={600} textAlign={'start'}>
							{/* {language?.upload} */}
							Upload&nbsp;
							{fileType}
						</Typography>
					</Grid>
					{/* <Grid item>
						<CustomSearch placeholder='Search...'/>
					</Grid> */}
					<Grid item>
						<IoIosClose
							onClick={isUploading ? undefined : handleClose}
							fontSize={'28px'}
							color={isUploading ? '#ccc' : '#000'}
							cursor={isUploading ? 'not-allowed' : 'pointer'}
							style={{ opacity: isUploading ? 0.5 : 1 }}
						/>
					</Grid>
				</Grid>
			</DialogTitle>
			<DialogContent sx={{ minHeight: allUploadOptionsDisabled ? '100%' : '450px' }} >
				{allUploadOptionsDisabled
					? (
						<Box
							width={'100%'}
							display={'flex'}
							flexDirection={'column'}
							alignItems={'center'}
							justifyContent={'center'}
							height={'100%'}
							py={4}
						>
							<NoData
								noDataHead='Upload Options Disabled'
								noDataPara='All upload options are currently disabled.'
								searchVal=''
								checkChangedDates
								data={[]}
							/>
						</Box>
					)
					: (
						<>
							<Box width={'100%'} sx={{ display: 'flex', justifyContent: 'center' }} >
								<CustomTabs
									value={tabValue}
									onChange={handleChange}
									sx={{
										backgroundColor : '#EFEFEF',
										paddingX        : 2,
										paddingY        : 0,
										borderRadius    : '13px',
										width           : { xs: '100%', sm: '70%' },
										opacity         : isUploading ? 0.5 : 1,
										pointerEvents   : isUploading ? 'none' : 'auto'
									}}
									aria-label='upload-tabs'
									variant='scrollable'
									scrollButtons='auto'
								>
									{enabledTabs.map((tab, index) => (
										<CustomTab
											key={tab.key}
											label={tab.label}
											id={`simple-tab-${index}`}
											aria-controls={`simple-tabpanel-${index}`}
										/>
									))}
								</CustomTabs>

							</Box>
							{/* system tab */}
							{isLocalUploadEnabled && (
								<TabPanel value={tabValue} index={systemTabIndex}>
									<SystemUpload
										setIsUploading={setIsUploading}
										isUploading={isUploading}
										note={note}
										onFileSelect={onFileSelect}
										onClose={onClose}
										fromCreation={fromCreation}
										fileType={fileType}
									/>
								</TabPanel>
							)}
							{/* media drive tab */}
							{isDriveUploadEnabled && (
								<TabPanel value={tabValue} index={mediaDriveTabIndex}>
									<MediaDrive fileType={fileType} userData={userData}
										selectedItems={selectedItems}
										setSelectedItems={setSelectedItems}
										singleSelection={fromCreation === 'article'}
									/>
								</TabPanel>
							)}
							{/* embed url tab */}
							{isEmbedUploadEnabled && (
								<TabPanel value={tabValue} index={urlTabIndex}>
									<EmbedLink fileType={fileType} Embednote={Embednote} onFileSelect={onFileSelect} onClose={onClose} fromCreation={fromCreation} />
								</TabPanel>
							)}
						</>
					)}
			</DialogContent>
			{selectedItems.length > 0 && (
				<Stack
					direction={'row'}
					sx={{
						justifyContent  : 'space-between',
						position        : 'absolute',
						bottom          : 0,
						width           : '100%',
						backgroundColor : '#424242',
						borderRadius    : '13px',
						alignItems      : 'center',
						paddingY        : '8px',
						paddingX        : '16px',
						boxShadow       : '0px 4px 10px rgba(18, 15, 15, 0.1)'
					}}
				>
					<Typography color={'#fff'} fontSize={14} fontWeight={500} borderRight={'1px solid #fff'} pr={3} >
						{selectedItems.length}
					</Typography>
					<Box sx={{ display: 'flex', gap: 5 }}>
						<Box
							component='button'
							sx={{
								cursor        : 'pointer',
								color         : '#fff',
								fontSize      : 14,
								fontWeight    : 500,
								background    : 'none',
								border        : 'none',
								padding       : 0,
								opacity       : isUploading ? 0.5 : 1,
								pointerEvents : isUploading ? 'none' : 'auto'
							}}
							onClick={handleClose}
						>
							Cancel
						</Box>
						<Box
							component='button'
							sx={{
								cursor        : 'pointer',
								color         : '#fff',
								fontSize      : 14,
								fontWeight    : 500,
								background    : 'none',
								border        : 'none',
								padding       : 0,
								opacity       : isUploading ? 0.5 : 1,
								pointerEvents : isUploading ? 'none' : 'auto',
								display       : 'flex',
								alignItems    : 'center',
								gap           : 1
							}}
							onClick={handleUpload}
						>
							{isUploading
								? (
									<>
										<span>Uploading</span>
										<Box sx={{ display: 'flex', gap: 0.5 }}>
											<Box
												sx={{
													width                 : 4,
													height                : 4,
													borderRadius          : '50%',
													backgroundColor       : '#fff',
													animation             : 'dotPulse 1.4s ease-in-out infinite both',
													'&:nth-of-type(1)'    : { animationDelay: '0s' },
													'&:nth-of-type(2)'    : { animationDelay: '0.2s' },
													'&:nth-of-type(3)'    : { animationDelay: '0.4s' },
													'@keyframes dotPulse' : {
														'0%, 80%, 100%' : {
															transform : 'scale(0.8)',
															opacity   : 0.5
														},
														'40%' : {
															transform : 'scale(1)',
															opacity   : 1
														}
													}
												}}
											/>
											<Box
												sx={{
													width                 : 4,
													height                : 4,
													borderRadius          : '50%',
													backgroundColor       : '#fff',
													animation             : 'dotPulse 1.4s ease-in-out infinite both',
													'&:nth-of-type(1)'    : { animationDelay: '0s' },
													'&:nth-of-type(2)'    : { animationDelay: '0.2s' },
													'&:nth-of-type(3)'    : { animationDelay: '0.4s' },
													'@keyframes dotPulse' : {
														'0%, 80%, 100%' : {
															transform : 'scale(0.8)',
															opacity   : 0.5
														},
														'40%' : {
															transform : 'scale(1)',
															opacity   : 1
														}
													}
												}}
											/>
											<Box
												sx={{
													width                 : 4,
													height                : 4,
													borderRadius          : '50%',
													backgroundColor       : '#fff',
													animation             : 'dotPulse 1.4s ease-in-out infinite both',
													'&:nth-of-type(1)'    : { animationDelay: '0s' },
													'&:nth-of-type(2)'    : { animationDelay: '0.2s' },
													'&:nth-of-type(3)'    : { animationDelay: '0.4s' },
													'@keyframes dotPulse' : {
														'0%, 80%, 100%' : {
															transform : 'scale(0.8)',
															opacity   : 0.5
														},
														'40%' : {
															transform : 'scale(1)',
															opacity   : 1
														}
													}
												}}
											/>
										</Box>
									</>
								)
								: (
									'Upload'
								)}
						</Box>
						<IoClose
							style={{
								cursor        : 'pointer',
								color         : '#fff',
								fontSize      : 20,
								fontWeight    : 500,
								opacity       : isUploading ? 0.5 : 1,
								pointerEvents : isUploading ? 'none' : 'auto'
							}}
							onClick={handleClose}
						/>
					</Box>
				</Stack>
			)}
		</Dialog>
	);
};

export default UploadDialog;
